/**
 * Apollo Client Configuration
 * GraphQL-only API client setup for web application
 * Uses cookie-based authentication (credentials included in requests)
 */
import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  HttpLink,
  InMemoryCache,
  split
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { createDecimalLink } from '@insio/decimal/apollo';
import { createTemporalLink } from '@insio/dates/apollo';
import type { IntrospectionQuery } from 'graphql';
import { typePolicies } from './apollo-type-policies';
import { notifySubscriptionsReconnected, subscriptionRetry } from './subscription-reconnect';
import {
  contextRequiredCode,
  isUnauthenticatedError,
  isUnauthenticatedGraphQLError,
  type ContextRequiredCode
} from './auth-error';
import introspectionResult from '../generated/introspection.json';
import { getConfig } from '../config';
import { getRequestLocale } from '../i18n/request-locale';
import {
  isOfficeClientRefusal,
  officeClientLink,
  officeClientWsGuardLink
} from './office-client-context';

let unauthenticatedCallback: (() => void) | null = null;

/** Register a callback that fires when any GraphQL request is answered as signed out. */
export function setUnauthenticatedCallback(fn: (() => void) | null): void {
  unauthenticatedCallback = fn;
}

let contextRequiredCallback: ((code: ContextRequiredCode) => void) | null = null;

/**
 * Register a callback that fires when a request is refused because the session acts in the other
 * context — a master account where an account is needed, or the reverse. Never a sign-out.
 */
export function setContextRequiredCallback(fn: ((code: ContextRequiredCode) => void) | null): void {
  contextRequiredCallback = fn;
}

/** Reports an unauthenticated response from outside GraphQL, e.g. a REST download. */
export function notifyUnauthenticated(): void {
  unauthenticatedCallback?.();
}

// Error handling link
const errorLink = new ErrorLink(({ error }) => {
  // Same rule as AuthContext: only a signed-out answer re-checks the session, never a 429 or 5xx.
  const isAuthError = isUnauthenticatedError(error);
  if (isAuthError) {
    unauthenticatedCallback?.();
  }

  const contextCode = contextRequiredCode(error);
  if (contextCode) {
    contextRequiredCallback?.(contextCode);
  }

  if (CombinedGraphQLErrors.is(error)) {
    const nonAuthErrors = error.errors.filter(e => !isUnauthenticatedGraphQLError(e));
    nonAuthErrors.forEach(({ message, locations, path }) =>
      // eslint-disable-next-line no-console
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    );
  } else if (CombinedProtocolErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) => {
      // eslint-disable-next-line no-console
      console.log(
        `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`
      );
      // Handle authentication errors
      if (extensions?.code === 'UNAUTHENTICATED') {
        // Redirect to login page
        // Note: Server will clear the HTTP-only cookie
        window.location.href = '/sign-in';
      }
    });
  } else if (!isAuthError && error.name !== 'AbortError' && !isOfficeClientRefusal(error)) {
    // A request its own caller aborted is not a failure, and a refused client request is shown by
    // its caller, so neither is reported here.
    // eslint-disable-next-line no-console
    console.error(`[Network error]: ${error}`);
  }
});

// HTTP link for GraphQL requests with cookie credentials
const httpLink = new HttpLink({
  uri: getConfig().graphqlUrl,
  credentials: 'include' // Send cookies with every request
});

const graphqlWsUrl = getConfig().graphqlUrl.replace(/^http/, 'ws');

const wsLink = new GraphQLWsLink(
  createClient({
    url: graphqlWsUrl,
    lazy: true,
    ...subscriptionRetry,
    on: {
      // A retried connection lost the events sent while it was down.
      connected: (_socket, _payload, wasRetry) => {
        if (wasRetry) notifySubscriptionsReconnected();
      }
    }
  })
);

// The office client header goes over HTTP only; an operation in a client is refused on the ws side.
const transportLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  ApolloLink.from([officeClientWsGuardLink, wsLink]),
  ApolloLink.from([officeClientLink, httpLink])
);

// Locale link — sends the current UI language on every request so
// the backend can translate server-side content (e.g. validation issues,
// pricing plan descriptions).
const localeLink = new ApolloLink((operation, forward) => {
  const locale = getRequestLocale();
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-locale': locale
    }
  }));
  return forward(operation);
});

// Scalar transformations at the GraphQL boundary:
//  - Decimal ↔ decimal.js Decimal
//  - Date ↔ Temporal.PlainDate
//  - DateTime ↔ Temporal.Instant
// The cast is needed because TS infers JSON imports as deeply literal types,
// which are structurally compatible with IntrospectionQuery but not assignable.
const introspection = introspectionResult as IntrospectionQuery;
const decimalLink = createDecimalLink({ schema: introspection });
const temporalLink = createTemporalLink({ schema: introspection });

// Create Apollo Client instance
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, localeLink, decimalLink, temporalLink, transportLink]),
  cache: new InMemoryCache({ typePolicies }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all'
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    },
    mutate: {
      errorPolicy: 'all'
    }
  }
});

export default apolloClient;
